import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  CountryPickerModalState,
  OpenCountryPickerProps,
} from '../../interfaces/connected-components/countryPickerModal';

const initialState: CountryPickerModalState = {
  isVisible: false,
  currentValue: 'en',
  filteredCountryIds: [],
  withSearch: false,
};

/**
 * CountryPickerModal Redux slice
 *
 * Optimized to prevent unnecessary state updates by:
 * 1. Only updating properties that changed
 * 2. Using immutable patterns efficiently
 */
export const countryPickerModalSlice = createSlice({
  name: 'countryPickerModal',
  initialState,
  reducers: {
    /**
     * Show the country picker modal with specified options
     */
    showCountryPickerModal: (
      state,
      action: PayloadAction<OpenCountryPickerProps>,
    ) => {
      // If modal is already visible with the same parameters, avoid state update
      if (
        state.isVisible &&
        state.currentValue === action.payload.currentValue &&
        state.withSearch === action.payload.withSearch &&
        JSON.stringify(state.filteredCountryIds) ===
          JSON.stringify(action.payload.filteredCountryIds || [])
      ) {
        // No state change needed - already in desired state
        return state;
      }

      // Otherwise update with new values
      return {
        ...state,
        isVisible: true,
        onSelectionChange: action.payload.onSelectionChange,
        currentValue: action.payload.currentValue,
        filteredCountryIds: action.payload.filteredCountryIds || [],
        withSearch: action.payload.withSearch,
      };
    },

    /**
     * Hide the country picker modal
     * - Only updates isVisible if needed
     */
    hideCountryPickerModal: state => {
      // If already hidden, don't update state
      if (!state.isVisible) {
        return state;
      }

      // Only update isVisible property
      return {
        ...state,
        isVisible: false,
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const {showCountryPickerModal, hideCountryPickerModal} =
  countryPickerModalSlice.actions;

export default countryPickerModalSlice.reducer;
